<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useCleAdvisorStore } from '@/stores/cleAdvisorStore';
import { useUserStore } from '@/stores/userStore';
import type { CreditProgressDto } from '@/services/cleAdvisorApi';

const store = useCleAdvisorStore();
const userStore = useUserStore();

const hasRecommendations = computed(() => userStore.featureFlags.cleAdvisor);

/** Build credit rows from required + earned + in-progress + registered, keyed by credit type. */
function getCreditRows(jp: CreditProgressDto) {
  // Start with required credit types, then add any types that appear in earned/in-progress/registered
  const types = new Map<string, boolean>();
  jp.requiredCredits?.forEach(c => types.set(c.creditTypeName, true));
  jp.earnedCredits.forEach(c => { if (!types.has(c.creditTypeName)) types.set(c.creditTypeName, false); });
  jp.inProgressCredits?.forEach(c => { if (!types.has(c.creditTypeName)) types.set(c.creditTypeName, false); });
  jp.registeredCredits?.forEach(c => { if (!types.has(c.creditTypeName)) types.set(c.creditTypeName, false); });
  if (types.size === 0) types.set('General', false);

  return Array.from(types.keys()).map(type => ({
    type,
    required: jp.requiredCredits?.find(c => c.creditTypeName === type)?.amount ?? null,
    earned: jp.earnedCredits.find(c => c.creditTypeName === type)?.amount ?? 0,
    inProgress: jp.inProgressCredits?.find(c => c.creditTypeName === type)?.amount ?? 0,
    registered: jp.registeredCredits?.find(c => c.creditTypeName === type)?.amount ?? 0,
  }));
}

const jurisdictionCards = computed(() =>
  store.summary.map(jp => ({
    ...jp,
    creditRows: getCreditRows(jp),
  }))
);

onMounted(() => {
  if (store.summary.length === 0 && !store.loading) {
    store.fetchSummary();
  }
});
</script>

<template>
  <div class="dashboard-section dashboard-section-full cle-advisor-dashboard">
    <div class="section-header">
      <h2>
        <bb-icon name="school" class="cle-advisor-icon" decorative></bb-icon>
        Credit Tracker
      </h2>
      <RouterLink v-if="hasRecommendations" to="/recommendations" class="section-link">View my Recommended Courses →</RouterLink>
    </div>

    <div v-if="store.loading" class="cle-advisor-loading">Loading credit progress...</div>

    <template v-else-if="jurisdictionCards.length > 0">
      <div class="cle-advisor-jurisdictions">
        <div
          v-for="jp in jurisdictionCards"
          :key="jp.jurisdictionId"
          class="cle-advisor-jp-card"
        >
          <!-- Header: Name + Deadline -->
          <div class="cle-advisor-jp-header">
            <div class="cle-advisor-jp-name">
              <strong>{{ jp.jurisdictionName }}</strong>
              <span class="cle-advisor-jp-code">{{ jp.jurisdictionCode }}</span>
            </div>
            <div v-if="jp.reportingDeadline" class="cle-advisor-jp-deadline">
              <bb-icon name="event" class="cle-advisor-meta-icon" decorative></bb-icon>
              Deadline: {{ jp.reportingDeadline }}
            </div>
          </div>

          <!-- Credit stats grid -->
          <div class="cle-stats-grid">
            <div v-for="row in jp.creditRows" :key="row.type" class="cle-stat-item">
              <div class="cle-stat-value" :class="{ 'has-credits': row.earned > 0 }">
                {{ row.earned.toFixed(1) }}<span v-if="row.required != null" class="cle-stat-required"> / {{ row.required }}</span>
              </div>
              <div class="cle-stat-label">{{ row.type }}</div>
              <div v-if="row.inProgress > 0 || row.registered > 0" class="cle-stat-sub">
                <span v-if="row.inProgress > 0" class="cle-stat-in-progress">{{ row.inProgress.toFixed(1) }} in progress</span>
                <span v-if="row.registered > 0" class="cle-stat-registered">{{ row.registered.toFixed(1) }} registered</span>
              </div>
            </div>
          </div>
          <div v-if="jp.compliancePeriodYears" class="cle-advisor-period">
            per {{ jp.compliancePeriodYears === 1 ? 'year' : jp.compliancePeriodYears + '-year period' }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cle-advisor-icon {
  font-size: 24px;
  vertical-align: middle;
  margin-right: 4px;
  color: var(--color-primary);
}

.cle-advisor-loading {
  text-align: center;
  padding: 24px;
  color: var(--color-text-secondary);
  font-size: 14px;
}

.cle-advisor-jurisdictions {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.cle-advisor-jp-card {
  padding: 14px 16px;
  background: var(--color-bg-page, #F8F9FA);
  border: 1px solid var(--color-border-light, #E8EAED);
  border-radius: var(--radius-md, 8px);
}

.cle-advisor-jp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.cle-advisor-jp-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.cle-advisor-jp-code {
  font-size: 11px;
  color: var(--color-text-tertiary);
  font-weight: 600;
}

.cle-advisor-jp-deadline {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: var(--color-text-secondary);
}

.cle-advisor-meta-icon {
  font-size: 14px;
}

.cle-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.cle-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 10px 8px;
  background: var(--color-bg-card, #fff);
  border-radius: var(--radius-md, 8px);
}

.cle-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-tertiary);
  line-height: 1.2;
}

.cle-stat-value.has-credits {
  color: var(--color-success, #10B981);
}

.cle-stat-required {
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--color-text-tertiary);
}

.cle-stat-label {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-top: 2px;
}

.cle-stat-sub {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  font-size: 11px;
}

.cle-stat-in-progress {
  color: var(--color-warning, #F59E0B);
  font-weight: 500;
}

.cle-stat-registered {
  color: var(--color-primary, #2A5488);
  font-weight: 500;
}

.cle-advisor-period {
  font-size: 11px;
  color: var(--color-text-tertiary);
  text-align: right;
  margin-top: 4px;
}
</style>
